// The local copy of the user's data (IndexedDB via Dexie) plus the outbox of changes still to send.

import Dexie, { type EntityTable } from "dexie";
import type { SlotKind } from "@shared/rhythm";
import type { SyncTable } from "@shared/sync";
import type { ChecklistCondition } from "@shared/evening";

interface SyncRow {
  id: string;
  space_id: string;
  version: number;
  deleted_at: string | null;
  updated_at: string;
  created_at: string;
}

export interface DayTemplate extends SyncRow {
  user_id: string;
  name: string;
  is_fallback: boolean;
  position: number;
  archived_at: string | null;
  pauses_routines: boolean;
  seed_key: string | null;
  morning_start: string | null;
  evening_start?: string | null;
}

interface BlockFields {
  title: string;
  slot_kind: SlotKind;
  start_local: string;
  end_local: string | null;
  group_title: string | null;
  is_optional: boolean;
  is_fixed: boolean;
  notify: boolean;
  lead_minutes: number;
  external_url: string | null;
  position: number;
}

export interface DayTemplateBlock extends SyncRow, BlockFields {
  template_id: string;
}

export interface DayTemplateRule extends SyncRow {
  user_id: string;
  template_id: string;
  weekdays: number[];
  valid_from: string | null;
  valid_to: string | null;
  priority: number;
}

export interface DayOverride extends SyncRow {
  user_id: string;
  starts_on: string;
  ends_on: string;
  template_id: string;
  reason: string | null;
  unplanned_entry_ids: string[];
  replaced: { id: string; starts_on: string; ends_on: string }[];
  split_ids: string[];
}

export interface DayPlan extends SyncRow {
  user_id: string;
  plan_date: string;
  template_id: string | null;
  template_name: string;
  status: "generated" | "planned" | "closed";
  energy?: "moe" | "gewoon" | "fit" | null;
}

export type BlockStatus = "open" | "done" | "skipped";

export interface DayBlock extends SyncRow, BlockFields {
  day_plan_id: string;
  template_block_id: string | null;
  plan_date: string;
  status: BlockStatus;
  completed_at: string | null;
}

export type TaskStatus = "inbox" | "open" | "someday" | "done" | "cancelled";

export interface Task extends SyncRow {
  user_id: string;
  title: string;
  notes: string | null;
  status: TaskStatus;
  due_on: string | null;
  estimate_minutes: 15 | 30 | 60 | null;
  completed_at: string | null;
  inbox_at: string;
  source: "app" | "share";
  position: number;
  category_id?: string | null;
  /** AI golf A (B31): pending, review (Te controleren), applied (put in place, can be undone), done. */
  ai_state?: "pending" | "review" | "applied" | "done" | null;
  ai_proposal?: import("@shared/ai").Understood | null;
  ai_applied?: { appointmentId?: string } | null;
  remind_at?: string | null;
}

export interface TaskStep extends SyncRow {
  task_id: string;
  title: string;
  position: number;
  checked_at: string | null;
}

export type PlanEntryStatus = "planned" | "done" | "moved" | "unplanned" | "skipped" | "expired";

export interface PlanEntry extends SyncRow {
  user_id: string;
  plan_date: string;
  /** Empty until the server created the day (planned offline on a day not on this device). */
  day_plan_id: string;
  day_block_id: string | null;
  kind: "task" | "routine";
  task_id: string | null;
  routine_id: string | null;
  occurrence_key: string | null;
  at_local?: string | null;
  /** Who put it there: manual, rule (the planner), proposal (PK said yes to a proposal or chose the day). */
  origin?: string;
  title: string;
  status: PlanEntryStatus;
  completed_at: string | null;
  carried_from_id: string | null;
  position: number;
  /** Local only, until the server row arrives: the day type block that was picked. */
  pending_template_block_id?: string | null;
}

export interface Routine extends SyncRow {
  user_id: string;
  title: string;
  schedule_type: "fixed" | "cadence" | "occasional";
  weekdays: number[];
  times_per_week: number | null;
  interval_days: number | null;
  slot_kinds: SlotKind[];
  duration_minutes: number;
  weekly_target: number | null;
  is_optional: boolean;
  last_done_on: string | null;
  dismissed_week: string | null;
  position: number;
  archived_at: string | null;
}

export interface RoutineCompletion extends SyncRow {
  user_id: string;
  routine_id: string;
  done_on: string;
  plan_entry_id: string | null;
  completed_at: string;
}

export interface ChecklistItem extends SyncRow {
  user_id: string;
  title: string;
  condition: ChecklistCondition;
  position: number;
}

export interface ChecklistCheck extends SyncRow {
  user_id: string;
  item_id: string;
  for_date: string;
  checked_at: string;
}

export interface EveningSession extends SyncRow {
  user_id: string;
  for_date: string;
  mode: "evening" | "quick";
  step: number;
  started_at: string;
  finished_at: string | null;
  duration_seconds: number | null;
}

export interface Appointment extends SyncRow {
  user_id: string;
  title: string;
  location: string | null;
  notes: string | null;
  start_local: string;
  duration_minutes: number;
  tz: string;
  repeat_kind: "none" | "weekly" | "monthly";
  repeat_interval: number;
  repeat_weekdays: number[];
  repeat_until: string | null;
  rrule: string | null;
  remind_minutes: number;
  visibility?: "private" | "busy" | "shared";
  updated_by?: string | null;
  travel_minutes?: number | null;
}

export interface AppointmentException extends SyncRow {
  appointment_id: string;
  recurrence_local: string;
  cancelled: boolean;
  new_start_local: string | null;
  new_duration_minutes: number | null;
  title: string | null;
  location: string | null;
}

export interface Person extends SyncRow {
  name: string;
  /** A household member with an account (Tsjits). */
  user_id?: string | null;
  position: number;
  seed_key: string | null;
}

export interface MealDefault extends SyncRow {
  user_id: string;
  weekday: number;
  person_id: string;
}

export interface Meal extends SyncRow {
  user_id: string;
  meal_date: string;
  place: "home" | "out";
  edited_at: string | null;
  dish?: string | null;
  recipe_id?: string | null;
  cook_user_id?: string | null;
  cook_person_id?: string | null;
  /** Golf 9: the dish is a proposal of this user until the meal's owner says yes. */
  dish_proposed_by?: string | null;
}

export interface MealParticipant extends SyncRow {
  meal_id: string;
  person_id: string;
}

export interface MorningStep extends SyncRow {
  user_id: string;
  title: string;
  minutes: number;
  position: number;
  template_ids: string[] | null;
  slot_kind: SlotKind;
  notify: boolean;
  part?: "morning" | "evening";
  is_fixed?: boolean;
}

export interface TaskCategory extends SyncRow {
  user_id: string;
  name: string;
  position: number;
  seed_key: string | null;
}

export interface WeeklyReview extends SyncRow {
  user_id: string;
  week_start: string;
  stats: {
    blocksDone: number;
    blocksTotal: number;
    gym: number;
    tasksDone: number;
    mostMissed: { title: string; n: number }[];
    /** Golf 9: how the days of the week felt ("Hoe was je dag?"). Missing in older reviews. */
    moods?: { good: number; ok: number; hard: number };
  };
  summary: string;
  tip: string;
}

export interface Recipe extends SyncRow {
  name: string;
  minutes: number | null;
  servings: number | null;
  ingredients: { name: string; amount: string | null }[];
  steps: string[];
  notes: string | null;
  source: "manual" | "ai";
}

export interface ShopCategory extends SyncRow {
  name: string;
  position: number;
  seed_key: string | null;
}

export interface Product extends SyncRow {
  name: string;
  category_id: string | null;
  use_count: number;
  last_used_at: string | null;
  is_staple?: boolean;
}

export interface ShoppingItem extends SyncRow {
  name: string;
  amount: string | null;
  category_id: string | null;
  product_id: string | null;
  recipe_id: string | null;
  checked_at: string | null;
  created_by?: string | null;
}

export interface Availability extends SyncRow {
  owner_id: string;
  avail_date: string;
  kind: "busy" | "away" | "appointment";
  start_local: string | null;
  end_local: string | null;
  title: string | null;
  location: string | null;
}

export interface Note extends SyncRow {
  user_id: string;
  title: string;
  body: string;
}

export interface DayReflection extends SyncRow {
  user_id: string;
  day: string;
  mood: 1 | 2 | 3;
  note: string | null;
}

export interface ImportantDate extends SyncRow {
  user_id: string;
  title: string;
  kind: "birthday" | "other";
  month: number;
  day: number;
  year: number | null;
  remind_days_before: number;
  ideas: string | null;
}

export interface SharedTodo extends SyncRow {
  title: string;
  note: string | null;
  done_at: string | null;
  created_by?: string | null;
}

export interface OutboxEntry {
  seq?: number;
  id: string;
  name: string;
  args: unknown;
  rowIds: string[];
  createdAt: string;
}

export interface MetaEntry {
  key: string;
  value: unknown;
}

export class LevenDb extends Dexie {
  day_templates!: EntityTable<DayTemplate, "id">;
  day_template_blocks!: EntityTable<DayTemplateBlock, "id">;
  day_template_rules!: EntityTable<DayTemplateRule, "id">;
  day_overrides!: EntityTable<DayOverride, "id">;
  day_plans!: EntityTable<DayPlan, "id">;
  day_blocks!: EntityTable<DayBlock, "id">;
  tasks!: EntityTable<Task, "id">;
  task_steps!: EntityTable<TaskStep, "id">;
  plan_entries!: EntityTable<PlanEntry, "id">;
  appointments!: EntityTable<Appointment, "id">;
  appointment_exceptions!: EntityTable<AppointmentException, "id">;
  routines!: EntityTable<Routine, "id">;
  routine_completions!: EntityTable<RoutineCompletion, "id">;
  checklist_items!: EntityTable<ChecklistItem, "id">;
  checklist_checks!: EntityTable<ChecklistCheck, "id">;
  evening_sessions!: EntityTable<EveningSession, "id">;
  people!: EntityTable<Person, "id">;
  meal_defaults!: EntityTable<MealDefault, "id">;
  meals!: EntityTable<Meal, "id">;
  meal_participants!: EntityTable<MealParticipant, "id">;
  morning_steps!: EntityTable<MorningStep, "id">;
  task_categories!: EntityTable<TaskCategory, "id">;
  weekly_reviews!: EntityTable<WeeklyReview, "id">;
  recipes!: EntityTable<Recipe, "id">;
  shop_categories!: EntityTable<ShopCategory, "id">;
  products!: EntityTable<Product, "id">;
  shopping_items!: EntityTable<ShoppingItem, "id">;
  availability!: EntityTable<Availability, "id">;
  notes!: EntityTable<Note, "id">;
  day_reflections!: EntityTable<DayReflection, "id">;
  important_dates!: EntityTable<ImportantDate, "id">;
  shared_todos!: EntityTable<SharedTodo, "id">;
  outbox!: EntityTable<OutboxEntry, "seq">;
  meta!: EntityTable<MetaEntry, "key">;

  constructor(name = "leven") {
    super(name);
    this.version(1).stores({
      day_templates: "id, user_id",
      day_template_blocks: "id, template_id",
      day_template_rules: "id, user_id",
      day_overrides: "id, user_id",
      day_plans: "id, plan_date",
      day_blocks: "id, day_plan_id, plan_date",
      outbox: "++seq, id",
      meta: "key",
    });
    this.version(2).stores({
      tasks: "id, status",
      task_steps: "id, task_id",
      plan_entries: "id, plan_date, task_id, status",
    });
    this.version(3).stores({
      appointments: "id",
      appointment_exceptions: "id, appointment_id",
    });
    this.version(4).stores({
      routines: "id",
      routine_completions: "id, routine_id, done_on",
      plan_entries: "id, plan_date, task_id, status, routine_id",
    });
    this.version(5).stores({
      checklist_items: "id",
      checklist_checks: "id, for_date, item_id",
      evening_sessions: "id, for_date",
    });
    this.version(6).stores({
      people: "id",
      meal_defaults: "id, weekday",
      meals: "id, meal_date",
      meal_participants: "id, meal_id",
    });
    this.version(7).stores({
      morning_steps: "id",
    });
    this.version(8).stores({
      task_categories: "id",
      tasks: "id, status, category_id",
    });
    this.version(9).stores({
      weekly_reviews: "id, week_start",
    });
    this.version(10).stores({
      recipes: "id",
      shop_categories: "id",
      products: "id",
      shopping_items: "id",
      availability: "id, avail_date, owner_id",
      notes: "id",
    });
    this.version(11).stores({
      day_reflections: "id, day",
      important_dates: "id",
      shared_todos: "id",
    });
  }

  syncTable(name: SyncTable) {
    return this.table<SyncRow & Record<string, unknown>, string>(name);
  }
}

export const db = new LevenDb();

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await db.meta.get(key))?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

/** Removes everything of the previous user (logout, or another user on this device). */
export async function clearLocalData(): Promise<void> {
  await db.transaction("rw", db.tables, async () => {
    await Promise.all(db.tables.map((t) => t.clear()));
  });
}
